"use client";

import { useQuery } from "@tanstack/react-query";
import type { MindstringPrototype } from "@/generated/fastapi";
import { type MeiliDocumentSearchHit } from "@/features/search";
import { sortSearchResultsByLatestAt, useMeiliRuntime } from "@/core/search";
import { thoughtKeys } from "../../queries/keys";

export const MAX_SUGGESTED_THOUGHTS_DISPLAY = 100;

type UseSuggestedThoughtsReviewQueryParams = {
  enabled?: boolean;
  languageFilters?: string[];
  limit?: number;
  originId?: string | null;
  prototype: MindstringPrototype;
  queryText?: string;
  refreshKey?: number;
  semanticRatio?: number;
  situationId?: string | null;
  sortBy?: string;
};

export default function useSuggestedThoughtsReviewQuery({
  enabled = true,
  languageFilters,
  limit = MAX_SUGGESTED_THOUGHTS_DISPLAY,
  originId,
  prototype,
  queryText,
  refreshKey = 0,
  semanticRatio = 0,
  situationId,
  sortBy,
}: UseSuggestedThoughtsReviewQueryParams) {
  const { searchThoughtDocuments } = useMeiliRuntime();

  return useQuery({
    queryKey: thoughtKeys.suggestedReview({
      languageFilters,
      limit,
      originId,
      prototype,
      queryText,
      refreshKey,
      semanticRatio,
      situationId,
      sortBy,
    }),
    queryFn: async () => {
      const thoughts = await searchThoughtDocuments<MeiliDocumentSearchHit>({
        indexName: "suggested_thoughts",
        languageFilters,
        limit,
        originId: originId ?? undefined,
        prototype,
        queryText: queryText?.trim() || undefined,
        semanticRatio,
        situationId: situationId ?? undefined,
        sortBy,
      });

      return sortBy ? thoughts : sortSearchResultsByLatestAt(thoughts, "desc");
    },
    enabled: enabled && Boolean(prototype),
  });
}
